/**
 * /chat-preview - the owner's private preview of the "Chat as Rogers" website chat. Embeds the real chat page
 * (/widget-ui?ws=<public id>&preview=1&mode=rogers), which the server only serves in Rogers mode to a signed-in member of the
 * workspace. Nothing on the website changes; conversations here are flagged as preview, and leads made here get the source
 * "site-agent-preview".
 */
import { useQuery } from "@tanstack/react-query";
import { useWorkspaceStore } from "@/hooks/use-workspace";
import { Loader2, ShieldCheck } from "lucide-react";

export default function ChatPreviewPage() {
  const { activeWorkspaceId } = useWorkspaceStore();
  const wsFromUrl = Number(new URLSearchParams(window.location.search).get("ws")) || null;
  const workspaceId = wsFromUrl || activeWorkspaceId;
  const { data: workspaces = [], isLoading } = useQuery<any[]>({ queryKey: ["/api/workspaces"] });
  const ws = workspaces.find((w: any) => w.id === workspaceId);
  const publicId: string | null = ws?.publicId || null;

  return (
    <div className="p-4 md:p-6 space-y-4 max-w-5xl">
      <div>
        <h1 className="text-xl font-semibold">Rogers chat preview</h1>
        <p className="text-sm text-muted-foreground mt-1">
          This is the chat your website visitors would see in Rogers mode{ws ? ` for ${ws.brandName || ws.name}` : ""}. It is private to you:
          your website keeps its current chat until you switch the mode in Lead Desk, Settings, Chat.
        </p>
      </div>
      <div className="flex items-start gap-2 rounded-md border bg-muted/40 p-3 text-xs text-muted-foreground max-w-xl">
        <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
        <span>
          Everything here is real: open times come from your calendar settings, a booking made here is a real booking, and a call-back
          request or hand-off appears in your Needs-you list (marked Preview). Use a test email address you control.
        </span>
      </div>
      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading...</div>
      ) : !publicId ? (
        <p className="text-sm text-destructive">Choose a workspace first.</p>
      ) : (
        <div className="rounded-2xl border shadow-lg overflow-hidden w-full max-w-[400px] h-[640px] bg-white">
          <iframe
            title="Rogers chat preview"
            src={`/widget-ui?ws=${encodeURIComponent(publicId)}&preview=1&mode=rogers`}
            className="w-full h-full border-0"
            data-testid="chat-preview-frame"
          />
        </div>
      )}
    </div>
  );
}
